import { useContext, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
	AlertCircle,
	ArrowLeft,
	Building2,
	CalendarRange,
	Check,
	ChevronDown,
	CircleDollarSign,
	Download,
	History,
	Infinity as InfinityIcon,
	Landmark,
	Pencil,
	ReceiptText,
	RefreshCw,
	Store,
	Undo2,
	UserRound,
	Users,
	WalletCards
} from 'lucide-react';
import BankAccount from '../components/BankAccount';
import { Dialog } from '../components/Dialog';
import { Button } from '../shadcn/Button';
import { cn } from '../shadcn/utils';
import { GlobalContext } from '../context/GlobalContext';
import useFetch, { callAPI } from '../hooks/api';
import { format_currency } from '../utils/formatters';
import { IdentityType } from '../utils/validate';
import {
	SellerCommissionCampaignSummary,
	SellerCommissionDashboard,
	SellerCommissionLedgerStats,
	SellerCommissionMovement,
	SellerCommissionPayoutRequest,
	SellerCommissionSellerDetail,
	SellerCommissionStoreDashboard
} from '../types/seller-commissions';

const payoutStatus: Record<SellerCommissionPayoutRequest['status'], string> = {
	requested: 'Solicitada',
	processing: 'Procesando',
	paid: 'Pagada',
	rejected: 'Rechazada'
};

const payoutStatusClass: Record<SellerCommissionPayoutRequest['status'], string> = {
	requested: 'bg-blue-500/10 text-blue-300',
	processing: 'bg-amber-500/10 text-amber-300',
	paid: 'bg-emerald-500/10 text-emerald-300',
	rejected: 'bg-red-500/10 text-red-300'
};

const campaignStatus: Record<SellerCommissionCampaignSummary['status'], string> = {
	upcoming: 'Programada',
	active: 'Activa',
	ended: 'Finalizada',
	stopped: 'Detenida'
};

const campaignStatusClass: Record<SellerCommissionCampaignSummary['status'], string> = {
	upcoming: 'bg-blue-500/10 text-blue-300',
	active: 'bg-emerald-500/10 text-emerald-300',
	ended: 'bg-neutral-800 text-neutral-300',
	stopped: 'bg-amber-500/10 text-amber-300'
};

const emptyStats: SellerCommissionLedgerStats = {
	sales_count: 0,
	gross_commissions: 0,
	reversed_count: 0,
	reversed_commissions: 0,
	net_commissions: 0
};

export default function SellerCommissions() {
	const { userData, internalCountry } = useContext(GlobalContext);
	const country = internalCountry.get() || userData?.country || 'venezuela';
	const [campaignId, setCampaignId] = useState('');
	const [selectedSeller, setSelectedSeller] = useState('');
	const pageCursors = useRef<Array<string | null>>([null]);
	const [message, setMessage] = useState<string | null>(null);
	const [requestDialogOpen, setRequestDialogOpen] = useState(false);
	const [requesting, setRequesting] = useState(false);
	const [bankLoading, setBankLoading] = useState(false);
	const { data, loading, error, setPayload, refetch } = useFetch('https://api.krece.app/storeprofile/seller_commissions/dashboard/', 'GET', !!userData, {
		batch_size: 25,
		page_index: 0
	});

	useEffect(() => {
		if (data?.selected_campaign_id && data.selected_campaign_id !== campaignId) {
			setCampaignId(data.selected_campaign_id);
		}
	}, [campaignId, data?.selected_campaign_id]);

	const resetPagination = () => {
		pageCursors.current = [null];
	};

	const selectCampaign = (nextCampaignId: string) => {
		setCampaignId(nextCampaignId);
		setSelectedSeller('');
		resetPagination();
		setPayload({ campaign_id: nextCampaignId, batch_size: 25, page_index: 0 });
	};

	const selectSeller = (sellerEmail: string) => {
		setSelectedSeller(sellerEmail);
		resetPagination();
		setPayload({
			campaign_id: data?.selected_campaign_id || campaignId,
			seller_email: sellerEmail,
			batch_size: 25,
			page_index: 0
		});
	};

	const closeSeller = () => {
		setSelectedSeller('');
		resetPagination();
		setPayload({
			campaign_id: data?.selected_campaign_id || campaignId,
			batch_size: 25,
			page_index: 0
		});
	};

	const changeMovementPage = ({
		direction,
		currentPage,
		nextCursor,
		hasMore,
		batchSize,
		sellerEmail
	}: {
		direction: 'previous' | 'next';
		currentPage: number;
		nextCursor: string | null;
		hasMore: boolean;
		batchSize: number;
		sellerEmail?: string;
	}) => {
		const targetPage = direction === 'next' ? currentPage + 1 : currentPage - 1;
		if (targetPage < 0 || (direction === 'next' && (!hasMore || !nextCursor))) return;
		const cursor = direction === 'next' ? nextCursor : pageCursors.current[targetPage] || null;
		if (direction === 'next') pageCursors.current[targetPage] = cursor;
		setPayload({
			campaign_id: data?.selected_campaign_id || campaignId,
			...(sellerEmail ? { seller_email: sellerEmail } : {}),
			batch_size: batchSize,
			...(cursor ? { cursor } : {}),
			page_index: targetPage
		});
	};

	const requestPayout = async () => {
		if (!userData || !data) return;
		setRequesting(true);
		const [, requestError] = await callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/payout_requests/',
			'POST',
			{ campaign_id: data.selected_campaign_id },
			userData,
			country
		);
		setRequesting(false);
		setRequestDialogOpen(false);
		if (requestError) {
			setMessage(requestError);
			return;
		}
		setMessage('Solicitud de desembolso enviada.');
		refetch();
	};

	const downloadReceipt = async (request: SellerCommissionPayoutRequest) => {
		if (!userData) return;
		const [receipt, receiptError] = await callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/payout_receipt/',
			'GET',
			{ id: request.id },
			userData,
			country
		);
		if (receiptError || !receipt) {
			setMessage(receiptError || 'No se pudo descargar el comprobante.');
			return;
		}
		const link = document.createElement('a');
		link.href = receipt.download_url;
		link.download = receipt.filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
	};

	const updateBankAccount = async (account: Parameters<Parameters<typeof BankAccount>[0]['onSubmit']>[0]) => {
		if (!userData) return false;
		setBankLoading(true);
		const [, bankError] = await callAPI('https://api.krece.app/storeprofile/seller_commissions/bank_account/', 'PUT', account, userData, country);
		setBankLoading(false);
		if (bankError) {
			setMessage(bankError);
			return false;
		}
		setMessage('Cuenta bancaria actualizada.');
		refetch();
		return true;
	};

	if (loading) return <CommissionDashboardSkeleton />;
	if (error === 'Esta tienda no tiene campañas de comisiones') return <Navigate to="/" replace />;
	if (error || !data) {
		return (
			<div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center gap-3 p-6 text-center">
				<div className="flex size-12 items-center justify-center rounded-xl bg-neutral-900 text-primary-300">
					<AlertCircle className="size-6" />
				</div>
				<h1 className="text-xl font-semibold tracking-tight text-neutral-100">Comisiones no disponibles</h1>
				<p className="text-sm text-neutral-400">{typeof error === 'string' ? error : 'Esta tienda no tiene campañas de comisiones.'}</p>
				<Button variant="secondary" className="!w-auto" onClick={refetch}>
					<RefreshCw className="size-4" /> Reintentar
				</Button>
			</div>
		);
	}

	const canRedeem = (data.role === 'seller' && data.payout_recipient === 'seller') || (data.role === 'master' && data.payout_recipient === 'master');
	const selectedCampaign = data.campaigns.find((campaign) => campaign.id === data.selected_campaign_id) || data.campaigns[0];
	const sellerDetail = data.role === 'master' ? data.seller_detail : null;
	const title = data.role === 'seller' ? 'Mis comisiones' : 'Comisiones de vendedores';

	return (
		<div className="min-h-full w-full space-y-5 p-4 md:p-6">
			{message && (
				<Dialog title="" onClose={() => setMessage(null)}>
					<p>{message}</p>
				</Dialog>
			)}
			{requestDialogOpen && (
				<Dialog title="Solicitar desembolso" onClose={() => setRequestDialogOpen(false)} cerrarButton={false} className="w-[min(480px,90vw)]">
					<div className="space-y-4">
						<p className="text-sm text-neutral-300">
							Se solicitarán <strong className="tabular-nums">{money(data.currency, data.summary.available_amount)}</strong> de{' '}
							<strong>{selectedCampaign?.name}</strong> a la cuenta {data.bank_account.masked_account_number || 'registrada'}.
						</p>
						<p className="rounded-lg bg-neutral-900/50 p-3 text-xs text-neutral-400">
							Esta solicitud no ejecuta una transferencia automática. Finanzas revisará y procesará el pago manualmente.
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setRequestDialogOpen(false)}>
								Cancelar
							</Button>
							<Button variant="primary" className="!w-auto" loading={requesting} onClick={requestPayout}>
								Confirmar solicitud
							</Button>
						</div>
					</div>
				</Dialog>
			)}

			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					{sellerDetail && <p className="mb-1 text-xs text-neutral-500">Comisiones de vendedores / {sellerDetail.branch_name}</p>}
					<h1 className="text-2xl font-semibold tracking-tight text-neutral-100">{sellerDetail ? sellerDetail.name : title}</h1>
					<p className="mt-1 text-sm text-neutral-400">
						{sellerDetail ? `${sellerDetail.email} · ${sellerDetail.branch_name}` : 'Consulta una campaña a la vez; los saldos nunca se mezclan.'}
					</p>
				</div>
				{sellerDetail && (
					<Button variant="secondary" className="!w-auto" onClick={closeSeller}>
						<ArrowLeft className="size-4" /> Volver al desglose
					</Button>
				)}
			</div>

			{!sellerDetail && (
				<>
					<LifetimeSummary data={data} />
					<CampaignPicker campaigns={data.campaigns} selectedId={data.selected_campaign_id} role={data.role} onSelect={selectCampaign} />
					<CampaignEarningSetup campaign={selectedCampaign} role={data.role} />
				</>
			)}

			{sellerDetail ? (
				<SellerDetail
					detail={sellerDetail}
					currency={data.currency}
					payoutRecipient={data.payout_recipient}
					onPage={(direction) =>
						changeMovementPage({
							direction,
							currentPage: sellerDetail.page_index,
							nextCursor: sellerDetail.next_cursor,
							hasMore: sellerDetail.has_more,
							batchSize: sellerDetail.batch_size,
							sellerEmail: selectedSeller
						})
					}
				/>
			) : data.role === 'branch' ? (
				<StoreDashboard data={data} selectedSeller="" onSelectSeller={() => undefined} />
			) : (
				<>
					<PayoutOwnerNotice role={data.role} recipient={data.payout_recipient} />
					<BalanceStrip data={data} canRedeem={canRedeem} onRequest={() => setRequestDialogOpen(true)} />

					{canRedeem && data.role === 'seller' && (
						<div className="grid gap-4 lg:grid-cols-2">
							<RequirementChecklist data={data} />
							<BankAccountCard
								data={data}
								email={userData?.email || ''}
								country={country}
								loading={bankLoading}
								setLoading={setBankLoading}
								onSubmit={updateBankAccount}
							/>
						</div>
					)}

					{canRedeem && data.role === 'master' && (
						<BankAccountCard
							data={data}
							email={userData?.email || ''}
							country={country}
							loading={bankLoading}
							setLoading={setBankLoading}
							onSubmit={updateBankAccount}
							compact
						/>
					)}

					{data.role === 'seller' ? (
						<MovementList
							movements={data.commissions}
							stats={data.stats || emptyStats}
							currency={data.currency}
							total={data.commissions_total}
							batchSize={data.batch_size}
							pageIndex={data.page_index}
							hasMore={data.has_more}
							onPage={(direction) =>
								changeMovementPage({
									direction,
									currentPage: data.page_index,
									nextCursor: data.next_cursor,
									hasMore: data.has_more,
									batchSize: data.batch_size
								})
							}
						/>
					) : (
						<StoreDashboard data={data} selectedSeller={selectedSeller} onSelectSeller={selectSeller} />
					)}

					{canRedeem && data.payout_requests.length > 0 && (
						<PayoutHistory requests={data.payout_requests} onDownloadReceipt={downloadReceipt} />
					)}
				</>
			)}
		</div>
	);
}

function LifetimeSummary({ data }: { data: SellerCommissionDashboard }) {
	const summary = data.lifetime_summary;
	if (!summary) return null;
	const title = data.role === 'seller' ? 'Tu histórico con Krece' : data.role === 'master' ? 'Histórico de la tienda con Krece' : 'Histórico de la sucursal con Krece';
	const totalLabel = data.role === 'seller' ? 'Total ganado' : 'Comisiones generadas';
	return (
		<details className="group overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
			<summary className="flex cursor-pointer list-none items-start justify-between gap-3 p-4">
				<div className="flex min-w-0 items-start gap-3">
					<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300">
						<History className="size-4" />
					</div>
					<div>
						<h2 className="font-medium text-neutral-100">{title}</h2>
						<p className="mt-1 text-xs text-neutral-500">Acumulado neto de todas las campañas, sin mezclar los saldos disponibles para retiro.</p>
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-3">
					<strong className="hidden tabular-nums text-primary-200 light:text-primary-700 sm:block">{signedMoney(data.currency, summary.net_commissions)}</strong>
					<ChevronDown className="size-4 text-neutral-500 transition-transform group-open:rotate-180" />
				</div>
			</summary>
			<div className="grid border-t border-neutral-800 sm:grid-cols-4">
				<LifetimeMetric label={totalLabel} value={signedMoney(data.currency, summary.net_commissions)} highlight />
				<LifetimeMetric label="Ventas comisionadas" value={String(summary.sales_count)} />
				<LifetimeMetric label="Campañas con ganancias" value={String(summary.campaign_count)} />
				<LifetimeMetric label="Reversos" value={`${summary.reversed_count} · −${money(data.currency, summary.reversed_commissions)}`} warning />
			</div>
		</details>
	);
}

function LifetimeMetric({ label, value, highlight = false, warning = false }: { label: string; value: string; highlight?: boolean; warning?: boolean }) {
	return (
		<div className="border-b border-neutral-800 p-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
			<MetricLabel>{label}</MetricLabel>
			<p className={cn('mt-2 text-xl font-semibold tracking-tight tabular-nums text-neutral-100', highlight && 'text-primary-200 light:text-primary-700', warning && 'text-amber-300')}>
				{value}
			</p>
		</div>
	);
}

export function CampaignEarningSetup({ campaign, role }: { campaign?: SellerCommissionCampaignSummary; role: SellerCommissionDashboard['role'] }) {
	if (!campaign?.plan || !campaign.currency || campaign.min_payout_amount === undefined) return null;
	const { currency, min_payout_amount: minPayoutAmount, plan } = campaign;
	const configuredTiers = plan.mode === 'sales_volume_tiers' ? plan.volume_tiers || [] : plan.tiers;
	const sellerCopy = role === 'seller';
	const payoutCopy = campaign.payout_recipient === 'master'
		? sellerCopy
			? 'La tienda master solicita el retiro y realiza la distribución.'
			: 'La tienda master solicita el retiro por el total de la campaña.'
		: sellerCopy
			? 'Tú solicitas el retiro de tu saldo individual.'
			: 'Cada vendedor solicita el retiro de su propio saldo.';
	return (
		<details className="group overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
			<summary className="flex cursor-pointer list-none items-start justify-between gap-3 p-4">
				<div className="flex min-w-0 items-start gap-3">
					<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300 light:text-primary-700">
						<CircleDollarSign className="size-4" />
					</div>
					<div className="min-w-0">
						<h2 className="font-medium text-neutral-100">{sellerCopy ? 'Cómo ganas en esta campaña' : 'Cómo se gana en esta campaña'}</h2>
						<p className="mt-1 truncate text-xs text-neutral-500">{campaign.name} · solo las ventas elegibles dentro de la vigencia generan comisión.</p>
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-3">
					<span className="hidden text-xs text-neutral-500 sm:block">
						{plan.mode === 'fixed_per_sale' ? `${money(currency, plan.fixed_amount)} por venta` : `${configuredTiers.length} ${configuredTiers.length === 1 ? 'rango' : 'rangos'}`}
					</span>
					<ChevronDown className="size-4 text-neutral-500 transition-transform group-open:rotate-180" />
				</div>
			</summary>
			{plan.mode === 'fixed_per_sale' ? (
				<div className="border-t border-neutral-800 px-4 py-5">
					<p className="text-sm text-neutral-400">{sellerCopy ? 'Ganas' : 'Se generan'}</p>
					<p className="mt-1 text-2xl font-semibold tracking-tight tabular-nums text-neutral-100">
						{money(currency, plan.fixed_amount)} <span className="text-sm font-normal text-neutral-500">por cada venta elegible</span>
					</p>
				</div>
			) : plan.mode === 'price_tiers' ? (
				<div className="border-t border-neutral-800">
					{plan.tiers.map((tier, index) => (
						<div key={`${tier.min_price}-${tier.max_price ?? 'plus'}-${index}`} className="flex items-center justify-between gap-4 border-b border-neutral-800 px-4 py-3 last:border-b-0">
							<div>
								<p className="text-sm font-medium text-neutral-200">Venta de {priceTierLabel(currency, tier.min_price, tier.max_price)}</p>
								<p className="mt-1 text-xs text-neutral-500">Precio registrado del producto</p>
							</div>
							<strong className="shrink-0 tabular-nums text-emerald-300 light:text-emerald-700">+{money(currency, tier.amount)}</strong>
						</div>
					))}
				</div>
			) : (
				<div className="border-t border-neutral-800">
					<div className="border-b border-neutral-800 px-4 py-3 text-xs leading-relaxed text-neutral-500">
						El conteo es individual por vendedor durante toda la campaña. Cada venta nueva usa el rango alcanzado y no recalcula las anteriores.
					</div>
					{(plan.volume_tiers || []).map((tier, index) => (
						<div key={`${tier.min_sales}-${tier.max_sales ?? 'plus'}-${index}`} className="flex items-center justify-between gap-4 border-b border-neutral-800 px-4 py-3 last:border-b-0">
							<div>
								<p className="text-sm font-medium text-neutral-200">Ventas {salesTierLabel(tier.min_sales, tier.max_sales)}</p>
								<p className="mt-1 text-xs text-neutral-500">Progresivo por vendedor</p>
							</div>
							<strong className="shrink-0 tabular-nums text-emerald-300 light:text-emerald-700">+{money(currency, tier.amount)}</strong>
						</div>
					))}
				</div>
			)}
			<div className="grid border-t border-neutral-800 sm:grid-cols-3">
				<div className="border-b border-neutral-800 p-4 sm:border-b-0 sm:border-r">
					<MetricLabel>Productos incluidos</MetricLabel>
					<p className="mt-2 text-sm text-neutral-300">{productCategoriesLabel(campaign.product_categories)}</p>
				</div>
				<div className="border-b border-neutral-800 p-4 sm:border-b-0 sm:border-r">
					<MetricLabel>Quién solicita el retiro</MetricLabel>
					<p className="mt-2 text-sm text-neutral-300">{payoutCopy}</p>
				</div>
				<div className="p-4">
					<MetricLabel>Mínimo durante la campaña</MetricLabel>
					<p className="mt-2 text-sm font-medium tabular-nums text-neutral-300">{money(currency, minPayoutAmount)}</p>
				</div>
			</div>
		</details>
	);
}

function CampaignPicker({
	campaigns,
	selectedId,
	role,
	onSelect
}: {
	campaigns: SellerCommissionCampaignSummary[];
	selectedId: string;
	role: SellerCommissionDashboard['role'];
	onSelect: (campaignId: string) => void;
}) {
	const drag = useRef({ pointerId: -1, startX: 0, startScroll: 0, moved: false });
	const [dragging, setDragging] = useState(false);

	const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		if (event.pointerType !== 'mouse' || event.button !== 0) return;
		drag.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startScroll: event.currentTarget.scrollLeft,
			moved: false
		};
	};
	const moveDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		if (drag.current.pointerId !== event.pointerId) return;
		const distance = event.clientX - drag.current.startX;
		if (!drag.current.moved && Math.abs(distance) > 4) {
			drag.current.moved = true;
			event.currentTarget.setPointerCapture(event.pointerId);
			setDragging(true);
		}
		if (!drag.current.moved) return;
		event.preventDefault();
		event.currentTarget.scrollLeft = drag.current.startScroll - distance;
	};
	const stopDrag = (event: React.PointerEvent<HTMLDivElement>) => {
		if (drag.current.pointerId !== event.pointerId) return;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
		drag.current.pointerId = -1;
		if (drag.current.moved) {
			window.setTimeout(() => {
				drag.current.moved = false;
			}, 0);
		}
		setDragging(false);
	};

	return (
		<section className="space-y-3">
			<div className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<CalendarRange className="size-4 text-primary-300" />
					<h2 className="font-medium text-neutral-100">Campañas</h2>
				</div>
				<span className="hidden text-xs text-neutral-500 sm:block">Más nueva a más antigua</span>
			</div>
			<div
				role="region"
				aria-label="Campañas ordenadas de la más nueva a la más antigua"
				tabIndex={0}
				className={cn(
					'-mx-4 flex snap-x snap-proximity gap-3 overflow-x-auto px-4 pb-2 outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 md:mx-0 md:px-0',
					dragging ? 'cursor-grabbing select-none' : 'cursor-grab'
				)}
				onPointerDown={startDrag}
				onPointerMove={moveDrag}
				onPointerUp={stopDrag}
				onPointerCancel={stopDrag}
				onClickCapture={(event) => {
					if (!drag.current.moved) return;
					event.preventDefault();
					event.stopPropagation();
					drag.current.moved = false;
				}}
				onDragStart={(event) => event.preventDefault()}
			>
				{[...campaigns]
					.sort(
						(a, b) =>
							new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime() ||
							a.name.localeCompare(b.name)
					)
					.map((campaign) => {
					const selected = campaign.id === selectedId;
					return (
						<button
							key={campaign.id}
							type="button"
							onClick={() => onSelect(campaign.id)}
							className={cn(
								'w-[300px] max-w-[82vw] shrink-0 snap-start rounded-xl border p-4 text-left transition-colors',
								selected
									? 'border-primary-500 bg-primary-500/5 ring-1 ring-primary-500/30'
									: 'border-neutral-800 bg-neutral-925/50 hover:border-neutral-700'
							)}
						>
							<div className="flex items-start justify-between gap-3">
								<p className="line-clamp-2 font-medium text-neutral-100">{campaign.name}</p>
								<span
									className={cn(
										'mt-1 size-3 shrink-0 rounded-full border',
										selected ? 'border-primary-300 bg-primary-500 ring-2 ring-primary-500/20' : 'border-neutral-700'
									)}
								/>
							</div>
							<p className="mt-2 text-xs tabular-nums text-neutral-500">
								{shortDate(campaign.starts_at)} — {campaign.ends_at ? shortDate(campaign.ends_at) : 'Sin vencimiento'}
							</p>
							<div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
								<span className={cn('rounded-md px-2 py-1', campaignStatusClass[campaign.status])}>{campaignStatus[campaign.status]}</span>
								{!campaign.ends_at && (
									<span className="inline-flex items-center gap-1 rounded-md bg-primary-500/10 px-2 py-1 text-primary-300">
										<InfinityIcon className="size-3.5" /> BAU
									</span>
								)}
								<span className="inline-flex items-center gap-1 text-neutral-400">
									{campaign.payout_recipient === 'master' ? <Store className="size-3.5" /> : <UserRound className="size-3.5" />}
									{campaign.payout_recipient === 'master'
										? role === 'master'
											? 'Cobra esta tienda'
											: 'Cobra la tienda master'
										: role === 'seller'
											? 'Cobras tú'
											: 'Cobra cada vendedor'}
								</span>
							</div>
						</button>
					);
				})}
			</div>
		</section>
	);
}

function PayoutOwnerNotice({ role, recipient }: { role: SellerCommissionDashboard['role']; recipient: 'seller' | 'master' }) {
	if ((role === 'seller' && recipient === 'seller') || (role === 'master' && recipient === 'master')) return null;
	const message =
		recipient === 'master'
			? 'La tienda master cobra el total y distribuye internamente. Puedes consultar tus resultados, pero no solicitar este saldo.'
			: role === 'master'
				? 'Cada vendedor solicita directamente sus comisiones de esta campaña.'
				: 'Los vendedores solicitan directamente sus comisiones de esta campaña.';
	return (
		<div className="flex items-start gap-3 rounded-xl border border-primary-500/20 bg-primary-500/5 p-4 text-sm text-neutral-300">
			{recipient === 'master' ? (
				<Store className="mt-0.5 size-4 shrink-0 text-primary-300" />
			) : (
				<UserRound className="mt-0.5 size-4 shrink-0 text-primary-300" />
			)}
			<p>{message}</p>
		</div>
	);
}

function BalanceStrip({ data, canRedeem, onRequest }: { data: SellerCommissionDashboard; canRedeem: boolean; onRequest: () => void }) {
	const summary = data.summary;
	const stats = data.stats || emptyStats;
	const requirements = payoutRequirements(summary, data.currency);
	const [openRequirement, setOpenRequirement] = useState<string | null>(null);
	const missing = requirements.filter((item) => !item.complete).length;
	const openRequest = data.payout_requests.find((request) => request.status === 'requested' || request.status === 'processing');
	if (!canRedeem)
		return (
			<section className="grid overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/60 sm:grid-cols-[1fr_1.4fr]">
				<div className="p-4 sm:border-r sm:border-neutral-800">
					<MetricLabel>Comisiones acumuladas</MetricLabel>
					<p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums text-neutral-100">{money(data.currency, summary.total_commissions)}</p>
					<p className="mt-2 text-xs text-neutral-500">
						{stats.sales_count} {stats.sales_count === 1 ? 'venta' : 'ventas'} · {stats.reversed_count} {stats.reversed_count === 1 ? 'reverso' : 'reversos'}
					</p>
				</div>
				<div className="flex items-start gap-3 border-t border-neutral-800 p-4 text-sm text-neutral-300 sm:border-t-0">
					<CircleDollarSign className="mt-0.5 size-4 shrink-0 text-primary-300" />
					<p>
						Este es un resultado de consulta. {data.payout_recipient === 'master' ? 'La tienda master solicita el total de la campaña.' : 'Cada vendedor solicita su propio saldo.'}
					</p>
				</div>
			</section>
		);
	return (
		<section className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/60">
			<div className="grid sm:grid-cols-3">
				<div className="order-2 border-t border-neutral-800 p-4 sm:order-1 sm:border-r sm:border-t-0">
					<MetricLabel>Acumuladas</MetricLabel>
					<p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-neutral-100">
						{money(data.currency, summary.total_commissions)}
					</p>
					<p className="mt-2 text-xs text-neutral-500">
						{stats.sales_count} {stats.sales_count === 1 ? 'venta' : 'ventas'} · {stats.reversed_count}{' '}
						{stats.reversed_count === 1 ? 'reverso' : 'reversos'}
					</p>
				</div>
				<div className="order-3 border-t border-neutral-800 p-4 sm:order-2 sm:border-r sm:border-t-0">
					<MetricLabel>Pagadas o en proceso</MetricLabel>
					<p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-neutral-100">{money(data.currency, summary.paid_or_pending)}</p>
					<p className="mt-2 text-xs text-neutral-500">{data.payout_requests.length} solicitudes en esta campaña</p>
				</div>
				<div className="order-1 border-b-2 border-primary-500 bg-primary-500/5 p-4 sm:order-3 sm:border-b-0 sm:border-l-2">
					<MetricLabel className="text-primary-300 light:text-primary-700">Disponibles para solicitar</MetricLabel>
					<div className="mt-2 flex flex-wrap items-center justify-between gap-3">
						<p className="text-3xl font-semibold tracking-tight tabular-nums text-primary-200 light:text-primary-700">{money(data.currency, summary.available_amount)}</p>
						{canRedeem && openRequest ? (
							<span className={cn('rounded-md px-2.5 py-1.5 text-xs', payoutStatusClass[openRequest.status])}>
								{payoutStatus[openRequest.status]} · {money(openRequest.currency, openRequest.amount)}
							</span>
						) : canRedeem ? (
							<Button variant="primary" className="!w-auto disabled:!opacity-70" disabled={!summary.can_request_payout} onClick={onRequest}>
								{summary.can_request_payout ? `Solicitar ${money(data.currency, summary.available_amount)}` : `Faltan ${missing} requisitos`}
							</Button>
						) : (
							<span className="text-xs text-neutral-400">
								Lo gestiona {data.payout_recipient === 'master' ? 'la tienda master' : 'cada vendedor'}
							</span>
						)}
					</div>
					{canRedeem && data.role === 'master' && (
						<div className="mt-3 space-y-2">
							<div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-400">
								{requirements.map((item) =>
									item.complete ? (
										<span key={item.label} className="inline-flex items-center gap-1 text-emerald-300">
											<Check className="size-3" /> {item.shortLabel}
										</span>
									) : (
										<button
											key={item.label}
											type="button"
											aria-expanded={openRequirement === item.label}
											onClick={() => setOpenRequirement((current) => (current === item.label ? null : item.label))}
											className="inline-flex items-center gap-1 rounded text-amber-300 outline-none hover:text-amber-200 focus-visible:ring-2 focus-visible:ring-amber-400/50"
										>
											<AlertCircle className="size-3" /> {item.shortLabel}
										</button>
									)
								)}
							</div>
							{openRequirement && (
								<p className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs leading-relaxed text-amber-300" role="status">
									{requirements.find((item) => item.label === openRequirement)?.message}
								</p>
							)}
						</div>
					)}
				</div>
			</div>
			{summary.campaign_balance < 0 && (
				<p className="flex items-start gap-2 border-t border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs text-amber-300">
					<AlertCircle className="mt-0.5 size-3.5 shrink-0" /> Esta campaña tiene un saldo negativo de −{money(data.currency, summary.campaign_balance)} por comisiones pagadas que luego fueron revertidas. Se descontará de comisiones posteriores.
				</p>
			)}
			<p className="border-t border-neutral-800 px-4 py-2 text-[11px] text-neutral-500">
				Disponibles = Acumuladas − Pagadas o en proceso
				{summary.cross_campaign_deduction > 0 ? ` − ${money(data.currency, summary.cross_campaign_deduction)} por reversos pagados de campañas anteriores` : ''}
			</p>
		</section>
	);
}

function RequirementChecklist({ data }: { data: SellerCommissionDashboard }) {
	const requirements = payoutRequirements(data.summary, data.currency);
	const completed = requirements.filter((item) => item.complete).length;
	return (
		<section id="payout-requirements" className="rounded-xl border border-neutral-800 bg-neutral-925/50 p-4">
			<div className="flex items-center justify-between gap-3">
				<h2 className="font-medium text-neutral-100">Requisitos para solicitar</h2>
				<span className={completed === requirements.length ? 'text-xs text-emerald-300' : 'text-xs text-amber-300'}>
					{completed} de {requirements.length}
				</span>
			</div>
			<div className="mt-4 space-y-3">
				{requirements.map((item) => (
					<div key={item.label} className="flex items-center justify-between gap-3 text-sm">
						<span className={item.complete ? 'inline-flex items-center gap-2 text-neutral-300' : 'inline-flex items-center gap-2 text-amber-300'}>
							{item.complete ? <Check className="size-4 text-emerald-400" /> : <AlertCircle className="size-4" />} {item.label}
						</span>
						{item.detail && <span className="text-xs tabular-nums text-neutral-500">{item.detail}</span>}
					</div>
				))}
			</div>
		</section>
	);
}

function BankAccountCard({
	data,
	email,
	country,
	loading,
	setLoading,
	onSubmit,
	compact = false
}: {
	data: SellerCommissionDashboard;
	email: string;
	country: string;
	loading: boolean;
	setLoading: (value: boolean) => void;
	onSubmit: Parameters<typeof BankAccount>[0]['onSubmit'];
	compact?: boolean;
}) {
	const account = data.bank_account;
	const editor = (label: string, Icon: typeof Pencil) => (
		<BankAccount
			email={email}
			country={country}
			loading={loading}
			setLoading={setLoading}
			onSubmit={onSubmit}
			requireTwentyDigitInterbankCode
			lockedIdentity={
				data.role === 'master' && account.identity_locked && account.document_type && account.identification_number
					? { type: account.document_type as IdentityType, number: account.identification_number }
					: undefined
			}
			trigger={(open) => (
				<Button variant="secondary" className="!w-auto" onClick={open}>
					<Icon className="size-4" /> {label}
				</Button>
			)}
		/>
	);
	if (account.is_complete) {
		return (
			<section
				id="bank-account"
				className={cn(
					'flex flex-col gap-3 rounded-xl border border-neutral-800 bg-neutral-925/50 p-4 sm:flex-row sm:items-center sm:justify-between',
					!compact && 'min-h-[132px]'
				)}
			>
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300">
						<Landmark className="size-5" />
					</div>
					<div className="min-w-0">
						<p className="font-medium text-neutral-100">Transferencia · {account.bank_name}</p>
						<p className="mt-1 truncate text-xs text-neutral-400">
							{account.masked_account_number} · {account.account_name} · {account.document_type?.toUpperCase()} {account.identification_number}
						</p>
					</div>
				</div>
				{editor('Editar', Pencil)}
			</section>
		);
	}
	return (
		<section id="bank-account" className="rounded-xl border border-neutral-800 bg-neutral-925/50 p-4">
			<h2 className="font-medium text-neutral-100">Cuenta bancaria</h2>
			<div className="mt-3 flex min-h-[118px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-700 bg-neutral-950/30 p-5 text-center">
				<Landmark className="size-5 text-neutral-500" />
				<p className="text-sm font-medium text-neutral-200">Aún no registras una cuenta</p>
				<p className="text-xs text-neutral-500">La necesitas para recibir el desembolso por transferencia.</p>
				{editor('Ingresar información bancaria', WalletCards)}
			</div>
		</section>
	);
}

export function StoreDashboard({
	data,
	selectedSeller,
	onSelectSeller
}: {
	data: Pick<SellerCommissionStoreDashboard, 'role' | 'currency' | 'branches'>;
	selectedSeller: string;
	onSelectSeller: (email: string) => void;
}) {
	const isMaster = data.role === 'master';
	const grandTotal = data.branches.reduce((sum, branch) => sum + branch.total_commissions, 0);
	return (
		<section className="space-y-3">
			<div className="flex items-center justify-between gap-3">
				<h2 className="font-medium text-neutral-100">{isMaster ? 'Sucursales y vendedores' : 'Vendedores de esta sucursal'}</h2>
				<span className="text-xs tabular-nums text-neutral-500">Total {money(data.currency, grandTotal)}</span>
			</div>
			{data.branches.map((branch) => {
				const branchShare = grandTotal > 0 ? (branch.total_commissions / grandTotal) * 100 : 0;
				const orderedSellers = [...branch.sellers].sort((a, b) => b.total_commissions - a.total_commissions || a.name.localeCompare(b.name));
				const sellerMaximum = Math.max(...orderedSellers.map((seller) => seller.total_commissions), 0);
				return (
					<details key={branch.email} open={!isMaster} className="group overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
						<summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4">
							<div className="flex min-w-0 items-center gap-3">
								<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300">
									<Building2 className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="truncate font-medium text-neutral-100">{branch.name}</p>
									<p className="truncate text-xs text-neutral-500">
										{branch.email} · {branch.sellers.length} vendedores
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<div className="hidden w-28 sm:block">
									<p className="mb-1 text-right text-[10px] tabular-nums text-neutral-500">{branchShare.toFixed(0)}% del total</p>
									<div className="h-1 overflow-hidden rounded bg-neutral-800">
										<div className="h-full bg-primary-500" style={{ width: `${Math.min(100, branchShare)}%` }} />
									</div>
								</div>
								<strong className="tabular-nums text-neutral-100">{money(data.currency, branch.total_commissions)}</strong>
								<ChevronDown className="size-4 text-neutral-500 transition-transform group-open:rotate-180" />
							</div>
						</summary>
						<div className="border-t border-neutral-800">
							{orderedSellers.map((seller) => {
								const width = sellerMaximum > 0 ? (seller.total_commissions / sellerMaximum) * 100 : 0;
								const inactive = seller.total_commissions === 0;
								return (
									<div
										key={seller.email}
										className={cn(
										'grid gap-3 border-b border-neutral-800 px-4 py-3 last:border-0 sm:grid-cols-[minmax(180px,1fr)_minmax(160px,2fr)_auto] sm:items-center',
										inactive && 'opacity-45'
									)}
									>
										<div className="flex min-w-0 items-center gap-3">
											<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-semibold text-neutral-300">
												{initials(seller.name)}
											</div>
											<div className="min-w-0">
												<p className="truncate text-sm font-medium text-neutral-100">{seller.name}</p>
												<p className="truncate text-xs text-neutral-500">{seller.email}</p>
											</div>
										</div>
										<div className="hidden h-1 overflow-hidden rounded bg-neutral-800 sm:block">
											<div className="h-full bg-primary-500" style={{ width: `${width}%` }} />
										</div>
										<div className="flex items-center justify-between gap-3 sm:justify-end">
											<span className="font-medium tabular-nums text-neutral-100">{money(data.currency, seller.total_commissions)}</span>
											{isMaster && (
												<Button
													variant="text"
													className="!w-auto !px-1 !py-1 text-xs"
													disabled={selectedSeller === seller.email || inactive || seller.detail_available === false}
													onClick={() => onSelectSeller(seller.email)}
												>
													Detalle
												</Button>
											)}
										</div>
									</div>
								);
							})}
							{!orderedSellers.length && <EmptyBlock icon={Users} title="Esta sucursal no tiene vendedores" />}
						</div>
					</details>
				);
			})}
			{!data.branches.length && <EmptyBlock icon={Store} title="No hay sucursales con vendedores" />}
		</section>
	);
}

export function SellerDetail({
	detail,
	currency,
	payoutRecipient,
	onPage
}: {
	detail: SellerCommissionSellerDetail;
	currency: string;
	payoutRecipient: 'seller' | 'master';
	onPage: (direction: 'previous' | 'next') => void;
}) {
	const stats = detail.stats || emptyStats;
	return (
		<div className="space-y-4">
			<section className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
				<div className="flex items-center gap-4 p-5">
					<div className="flex size-12 shrink-0 items-center justify-center rounded-full border border-primary-500/40 bg-primary-500/10 font-semibold text-primary-200 light:text-primary-700">
						{initials(detail.name)}
					</div>
					<div className="min-w-0">
						<h2 className="font-semibold text-neutral-100">{detail.name}</h2>
						<p className="truncate text-sm text-neutral-500">
							{detail.email} · {detail.branch_name}
						</p>
					</div>
				</div>
				<div className="grid border-t border-neutral-800 sm:grid-cols-4">
					<DetailStat label="Total en esta campaña" value={money(currency, detail.total_commissions)} highlight />
					<DetailStat label="Ventas comisionadas" value={String(stats.sales_count)} />
					<DetailStat label="Comisiones generadas" value={`+${money(currency, stats.gross_commissions)}`} positive />
					<DetailStat label="Reversos" value={`${stats.reversed_count} · −${money(currency, stats.reversed_commissions)}`} warning />
				</div>
			</section>
			<MovementTable movements={detail.commissions} stats={stats} currency={currency} showPrice showRule />
			<Pagination
				total={detail.commissions_total}
				batchSize={detail.batch_size}
				pageIndex={detail.page_index}
				hasMore={detail.has_more}
				onPage={onPage}
			/>
			<p className="flex items-start gap-2 text-xs text-neutral-500">
				<AlertCircle className="mt-0.5 size-3.5 shrink-0" /> Vista de solo lectura. Los reversos conservan el movimiento original y descuentan
				exactamente la comisión guardada; {payoutRecipient === 'master' ? 'el desembolso lo gestiona la tienda master' : 'cada vendedor gestiona su propio desembolso'}.
			</p>
		</div>
	);
}

function DetailStat({
	label,
	value,
	highlight,
	positive,
	warning
}: {
	label: string;
	value: string;
	highlight?: boolean;
	positive?: boolean;
	warning?: boolean;
}) {
	return (
		<div className="border-b border-neutral-800 p-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
			<MetricLabel className={highlight ? 'text-primary-300 light:text-primary-700' : ''}>{label}</MetricLabel>
			<p className={cn('mt-2 text-xl font-semibold tabular-nums', positive ? 'text-emerald-300 light:text-emerald-700' : warning ? 'text-amber-300 light:text-amber-700' : 'text-neutral-100')}>
				{value}
			</p>
		</div>
	);
}

function MovementList({
	movements,
	stats,
	currency,
	total,
	batchSize,
	pageIndex,
	hasMore,
	onPage
}: {
	movements: SellerCommissionMovement[];
	stats: SellerCommissionLedgerStats;
	currency: string;
	total: number;
	batchSize: number;
	pageIndex: number;
	hasMore: boolean;
	onPage: (direction: 'previous' | 'next') => void;
}) {
	return (
		<section className="space-y-3">
			<MovementTable movements={movements} stats={stats} currency={currency} showPrice showRule />
			<Pagination total={total} batchSize={batchSize} pageIndex={pageIndex} hasMore={hasMore} onPage={onPage} />
		</section>
	);
}

function MovementTable({
	movements,
	stats,
	currency,
	showPrice = false,
	showRule = false
}: {
	movements: SellerCommissionMovement[];
	stats: SellerCommissionLedgerStats;
	currency: string;
	showPrice?: boolean;
	showRule?: boolean;
}) {
	return (
		<section className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 px-4 py-3">
				<h2 className="font-medium text-neutral-100">Movimientos</h2>
				<p className="text-xs tabular-nums text-neutral-500">
					{stats.sales_count} {stats.sales_count === 1 ? 'venta' : 'ventas'} ·{' '}
					<span className="text-amber-300 light:text-amber-700">
						{stats.reversed_count} {stats.reversed_count === 1 ? 'reverso' : 'reversos'} −{money(currency, stats.reversed_commissions)}
					</span>{' '}
					· Neto <strong className="text-neutral-200">{money(currency, stats.net_commissions)}</strong>
				</p>
			</div>
			<div
				className="hidden gap-4 border-b border-neutral-800 bg-neutral-900/30 px-4 py-3 text-[10px] font-medium uppercase tracking-wider text-neutral-500 sm:grid"
				style={{
					gridTemplateColumns: showRule
						? 'minmax(160px,1.3fr) minmax(150px,1fr) auto minmax(150px,1fr) minmax(130px,1fr) auto'
						: showPrice
							? 'minmax(160px,1.3fr) minmax(150px,1fr) auto minmax(130px,1fr) auto'
							: 'minmax(180px,1.4fr) minmax(170px,1fr) minmax(130px,1fr) auto'
				}}
			>
				<span>Equipo</span>
				<span>Tipo</span>
				{showPrice && <span>Precio</span>}
				{showRule && <span>Rango aplicado</span>}
				<span>Fecha</span>
				<span className="text-right">Comisión</span>
			</div>
			{movements.map((movement) => {
				const reversedOriginal = movement.movement === 'earned' && !!movement.reversed_at;
				return (
					<div
						key={`${movement.device_id}-${movement.movement}-${movement.date}`}
						className={cn('border-b border-neutral-800 px-4 py-3 last:border-0', reversedOriginal && 'opacity-50')}
					>
						<div className="grid gap-3 sm:hidden">
							<div className="flex items-start justify-between gap-3">
								<div>
									<p className="font-medium text-neutral-100">{movement.device_model}</p>
									<p className="text-xs text-neutral-500">{movement.device_id}</p>
								</div>
								<MovementAmount movement={movement} currency={currency} />
							</div>
							<MovementType movement={movement} />
							<div className="flex items-end justify-between gap-3 text-xs text-neutral-500">
								<div>
									{showPrice && <p>Precio {money(currency, movement.device_price)}</p>}
									{showRule && <p>{commissionRuleLabel(movement)}</p>}
								</div>
								<time className="text-right">{formatDateTime(movement.date)}</time>
							</div>
						</div>
						<div
							className="hidden items-center gap-4 text-sm sm:grid"
							style={{
								gridTemplateColumns: showRule
									? 'minmax(160px,1.3fr) minmax(150px,1fr) auto minmax(150px,1fr) minmax(130px,1fr) auto'
									: showPrice
										? 'minmax(160px,1.3fr) minmax(150px,1fr) auto minmax(130px,1fr) auto'
										: 'minmax(180px,1.4fr) minmax(170px,1fr) minmax(130px,1fr) auto'
							}}
						>
							<div className="min-w-0">
								<p className="truncate font-medium text-neutral-100">{movement.device_model}</p>
								<p className="truncate text-xs text-neutral-500">{movement.device_id}</p>
							</div>
							<MovementType movement={movement} />
							{showPrice && <span className="tabular-nums text-neutral-300">{money(currency, movement.device_price)}</span>}
							{showRule && <span className="text-xs text-neutral-500">{commissionRuleLabel(movement)}</span>}
							<time className="text-xs text-neutral-500">{formatDateTime(movement.date)}</time>
							<MovementAmount movement={movement} currency={currency} />
						</div>
					</div>
				);
			})}
			{!movements.length && <EmptyBlock icon={ReceiptText} title="Todavía no hay comisiones en esta campaña" />}
		</section>
	);
}

function MovementType({ movement }: { movement: SellerCommissionMovement }) {
	if (movement.movement === 'reversed') {
		return (
			<div className="text-xs">
				<p className="inline-flex items-center gap-1.5 font-medium text-amber-300 light:text-amber-700">
					<Undo2 className="size-3.5" /> Venta revertida
				</p>
				<p className="mt-1 text-neutral-500">
					Anula la venta del {movement.original_sale_date ? shortDate(movement.original_sale_date) : 'registro original'}
				</p>
			</div>
		);
	}
	return (
		<div className="text-xs">
			<p className="inline-flex items-center gap-1.5 font-medium text-emerald-300 light:text-emerald-700">
				<Check className="size-3.5" /> Venta comisionada
			</p>
			{movement.reversed_at && <p className="mt-1 text-amber-400 light:text-amber-700">Revertida el {shortDate(movement.reversed_at)}</p>}
		</div>
	);
}

function MovementAmount({ movement, currency }: { movement: SellerCommissionMovement; currency: string }) {
	if (movement.movement === 'reversed')
		return <span className="text-right font-semibold tabular-nums text-red-400 light:text-red-700">−{money(currency, movement.amount)}</span>;
	if (movement.reversed_at)
		return <span className="text-right font-medium tabular-nums text-neutral-500 line-through">+{money(currency, movement.amount)}</span>;
	return <span className="text-right font-semibold tabular-nums text-emerald-300 light:text-emerald-700">+{money(currency, movement.amount)}</span>;
}

function PayoutHistory({
	requests,
	onDownloadReceipt
}: {
	requests: SellerCommissionPayoutRequest[];
	onDownloadReceipt: (request: SellerCommissionPayoutRequest) => void;
}) {
	return (
		<section className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
			<h2 className="border-b border-neutral-800 px-4 py-3 font-medium text-neutral-100">Solicitudes de esta campaña</h2>
			<div className="hidden grid-cols-[1fr_1fr_1fr_2fr] border-b border-neutral-800 bg-neutral-900/30 px-4 py-3 text-[10px] font-medium uppercase tracking-wider text-neutral-500 sm:grid">
				<span>Monto</span>
				<span>Solicitada</span>
				<span>Estado</span>
				<span>Detalle</span>
			</div>
			{requests.map((request) => (
				<div
					key={request.id}
					className="grid gap-2 border-b border-neutral-800 px-4 py-3 text-sm last:border-0 sm:grid-cols-[1fr_1fr_1fr_2fr] sm:items-center"
				>
					<strong className="tabular-nums text-neutral-100">{money(request.currency, request.amount)}</strong>
					<time className="text-xs text-neutral-500">{formatDateTime(request.requested_at)}</time>
					<div>
						<span className={cn('rounded-md px-2 py-1 text-xs', payoutStatusClass[request.status])}>{payoutStatus[request.status]}</span>
					</div>
					<div className="space-y-2 text-xs text-neutral-500">
						<p className={request.status === 'rejected' ? 'text-red-300' : ''}>
							{request.status === 'rejected'
								? `Motivo del rechazo: ${request.notes || 'No especificado'}`
								: request.notes ||
									`${request.bank_account_snapshot?.bank_name || 'Transferencia manual'} · ${request.bank_account_snapshot?.masked_account_number || request.bank_account_snapshot?.account_number || 'cuenta registrada'}`}
						</p>
						{request.receipt && (
							<button type="button" onClick={() => onDownloadReceipt(request)} className="inline-flex items-center gap-1.5 text-primary-300 hover:text-primary-200">
								<Download className="size-3.5" /> Descargar comprobante PDF
							</button>
						)}
					</div>
				</div>
			))}
		</section>
	);
}

function Pagination({
	total,
	batchSize,
	pageIndex,
	hasMore,
	onPage
}: {
	total: number;
	batchSize: number;
	pageIndex: number;
	hasMore: boolean;
	onPage: (direction: 'previous' | 'next') => void;
}) {
	if (total <= batchSize) return null;
	const pages = Math.ceil(total / batchSize);
	return (
		<div className="flex flex-wrap items-center justify-end gap-2 text-xs text-neutral-500">
			<Button variant="secondary" className="!w-auto" disabled={pageIndex === 0} onClick={() => onPage('previous')}>
				Anterior
			</Button>
			<span className="px-2 tabular-nums">
				Página {pageIndex + 1} de {pages}
			</span>
			<Button variant="secondary" className="!w-auto" disabled={!hasMore} onClick={() => onPage('next')}>
				Siguiente
			</Button>
		</div>
	);
}

function MetricLabel({ children, className = '' }: { children: string; className?: string }) {
	return <p className={cn('text-[10px] font-semibold uppercase tracking-wider text-neutral-500', className)}>{children}</p>;
}

function EmptyBlock({ icon: Icon, title }: { icon: typeof Store; title: string }) {
	return (
		<div className="flex min-h-[120px] flex-col items-center justify-center gap-2 p-6 text-center text-neutral-500">
			<Icon className="size-5" />
			<p className="text-sm">{title}</p>
		</div>
	);
}

function CommissionDashboardSkeleton() {
	return (
		<div className="min-h-full w-full animate-pulse space-y-5 p-4 md:p-6">
			<div className="h-8 w-64 rounded-lg bg-neutral-800/60" />
			<div className="h-4 w-96 max-w-full rounded bg-neutral-800/40" />
			<div className="grid gap-3 sm:grid-cols-3">
				{[1, 2, 3].map((item) => (
					<div key={item} className="h-28 rounded-xl bg-neutral-900/50" />
				))}
			</div>
			<div className="h-32 rounded-xl bg-neutral-900/50" />
			<div className="h-72 rounded-xl bg-neutral-900/50" />
		</div>
	);
}

function payoutRequirements(summary: SellerCommissionDashboard['summary'], currency: string) {
	const hasBalance = summary.has_available_balance ?? summary.available_amount > 0;
	const minimumReached = summary.minimum_waived || (summary.minimum_reached ?? summary.available_amount >= summary.min_payout_amount);
	const noOpenRequest = !(summary.has_open_request ?? false);
	const bankComplete = summary.bank_account_complete ?? false;
	return [
		{
			label: 'Saldo disponible',
			shortLabel: 'Saldo',
			complete: hasBalance,
			detail: money(currency, summary.available_amount),
			message: hasBalance
				? `Saldo disponible: ${money(currency, summary.available_amount)}.`
				: 'Debes acumular comisiones antes de solicitar un retiro.'
		},
		{
			label: summary.minimum_waived ? 'Campaña cerrada: mínimo liberado' : 'Mínimo para solicitar retiro alcanzado',
			shortLabel: summary.minimum_waived ? 'Sin mínimo' : 'Mínimo',
			complete: minimumReached,
			detail: summary.minimum_waived ? 'Cualquier saldo' : money(currency, summary.min_payout_amount),
			message: summary.minimum_waived
				? 'La campaña finalizó: puedes retirar cualquier saldo positivo pendiente.'
				: `Mínimo: ${money(currency, summary.min_payout_amount)} para solicitar un retiro. Saldo disponible: ${money(currency, summary.available_amount)}.`
		},
		{
			label: 'Sin solicitudes abiertas en esta campaña',
			shortLabel: 'Sin solicitud abierta',
			complete: noOpenRequest,
			detail: '',
			message: noOpenRequest ? 'No tienes solicitudes abiertas.' : 'Ya existe una solicitud de retiro en proceso para esta campaña.'
		},
		{
			label: 'Cuenta bancaria registrada',
			shortLabel: 'Cuenta bancaria',
			complete: bankComplete,
			detail: bankComplete ? 'Lista' : 'Registrar',
			message: bankComplete ? 'La cuenta bancaria está lista.' : 'Registra una cuenta bancaria completa para solicitar el retiro.'
		}
	];
}

function commissionRuleLabel(movement: SellerCommissionMovement) {
	const rule = movement.commission_rule;
	if (!rule) return 'Regla guardada en la venta';
	if (rule.mode === 'fixed_per_sale') return `Monto fijo · ${money(rule.currency, rule.amount)}`;
	if (rule.mode === 'sales_volume_tiers')
		return `Venta ${rule.sale_number || '?'} · rango ${salesTierLabel(rule.min_sales || 1, rule.max_sales ?? null)} · ${money(rule.currency, rule.amount)}`;
	if (rule.max_price === null) return `Desde ${money(rule.currency, rule.min_price || 0)} · ${money(rule.currency, rule.amount)}`;
	return `${money(rule.currency, rule.min_price || 0)}–${money(rule.currency, rule.max_price || 0)} · ${money(rule.currency, rule.amount)}`;
}

function initials(value: string) {
	return (
		value
			.split(/\s+/)
			.filter(Boolean)
			.slice(0, 2)
			.map((part) => part[0]?.toUpperCase())
			.join('') || '—'
	);
}

function money(currency: string, amount: number) {
	return `${currency}${format_currency(Math.abs(amount))}`;
}

function signedMoney(currency: string, amount: number) {
	return amount < 0 ? `−${money(currency, amount)}` : money(currency, amount);
}

function priceTierLabel(currency: string, minimum: number, maximum: number | null) {
	return maximum === null ? `${money(currency, minimum)} en adelante` : `${money(currency, minimum)} a ${money(currency, maximum)}`;
}

function salesTierLabel(minimum: number, maximum: number | null) {
	return maximum === null ? `${minimum} en adelante` : `${minimum} a ${maximum}`;
}

function productCategoriesLabel(categories: SellerCommissionCampaignSummary['product_categories']) {
	const labels = {
		phones: 'Celulares (Android y tablets)',
		iphones: 'iPhones',
		other: 'Otros productos'
	};
	return categories.map((category) => labels[category]).join(' · ');
}

function shortDate(value: string | null) {
	if (!value) return 'Sin vencimiento';
	return new Date(value).toLocaleDateString([], {
		month: 'short',
		day: 'numeric',
		year: new Date(value).getFullYear() === new Date().getFullYear() ? undefined : 'numeric'
	});
}

function formatDateTime(value: string) {
	return new Date(value).toLocaleString([], {
		dateStyle: 'medium',
		timeStyle: 'short'
	});
}
